import React, { useEffect, useMemo, useState } from 'react'
import { BoxArrowDown, BrowserChrome, BrowserSafari, Check, PlusSquare, ThreeDotsVertical, Upload } from 'react-bootstrap-icons'
import logo from "../assets/iRise-psychology-Logo1.png"

export default function Download() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [canPromptInstall, setCanPromptInstall] = useState(false)
  const [installResult, setInstallResult] = useState('')
  const [installMessage, setInstallMessage] = useState('')

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setCanPromptInstall(true)
      setInstallMessage('')
    }

    window.addEventListener('beforeinstallprompt', handler)

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const platform = useMemo(() => {
    const ua = navigator.userAgent || ''
    const isIOS = /iPad|iPhone|iPod/.test(ua)
    const isAndroid = /Android/.test(ua)
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua)
    const isDesktop = !isIOS && !isAndroid

    const isStandalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true

    return { isIOS, isAndroid, isSafari, isDesktop, isStandalone }
  }, [])

  const getFailReason = () => {
    if (platform.isStandalone) return 'This app is already installed and opened in app mode.'
    if (!window.isSecureContext) return 'Install requires https on the live site.'
    if (platform.isIOS) {
      if (!platform.isSafari) return 'On iPhone and iPad, install works in Safari using Add to Home Screen.'
      return 'On iPhone and iPad, use Share then Add to Home Screen.'
    }
    return 'Install prompt is not available. Try Chrome or Edge, or use the browser menu option.'
  }

  const handleInstall = async () => {
    setInstallResult('')
    setInstallMessage('')

    if (deferredPrompt) {
      try {
        deferredPrompt.prompt()
        const choice = await deferredPrompt.userChoice
        setDeferredPrompt(null)
        setCanPromptInstall(false)

        if (choice?.outcome === 'accepted') setInstallResult('Install started')
        else setInstallResult('Install dismissed')
        return
      } catch {
        setInstallMessage('Install prompt failed. Use the browser menu option to install.')
        return
      }
    }

    setInstallMessage(getFailReason())
  }

  return (
    <div className="bg-dark min-vh-100 d-flex flex-column">
      <div className="section-tint flex-grow-1">
        <div className="bg-white py-4 py-lg-5">
          <div className="main-wrap">
            <div className="row align-items-center g-4">
              <div className="col-12 col-lg-8">
                <div className="d-flex align-items-center gap-2 mb-2 hero-logo">
                     <img
                src={logo}
                alt="iRise Psychology"
                className="navbar-brand-logo"
              />
                </div>

                <h1 className="display-6 fw-bold brand-text-dark mb-2">Download the app</h1>
                <p className="brand-text-gray mb-0">
                  Install to get quicker access and an app style experience.
                </p>
              </div>

              <div className="col-12 col-lg-4">
                <div className="hero-cta d-grid gap-2 ms-lg-auto">
                  <button
                    type="button"
                    className="btn btn-brand-primary btn-lg"
                    onClick={handleInstall}
                  >
                    Install app
                  </button>

                  <a className="btn btn-outline-brand btn-lg" href="/" role="button">
                    Back to site
                  </a>

                  {installResult ? (
                    <div className="small text-center brand-text-gray">{installResult}</div>
                  ) : null}

                  {installMessage ? (
                    <div className="alert alert-light border mb-0">
                      <div className="fw-semibold brand-text-dark mb-1">Install not available</div>
                      <div className="small brand-text-gray mb-0">{installMessage}</div>
                    </div>
                  ) : null}

                  {!canPromptInstall && !installMessage && !installResult ? (
                    <div className="small text-center brand-text-gray">
                      If Install app is not available, use the instructions below.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="main-wrap py-4 py-lg-5">
          <div className="row g-4">
            <div className="col-12 col-lg-7">
              <div className="bg-white border rounded-4 shadow-sm">
                <div className="p-4 p-lg-5">
                  <h2 className="h4 fw-semibold brand-text-dark mb-3">How to install</h2>

                  <ul className="nav nav-pills gap-2 mb-4" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${platform.isIOS ? 'active' : ''}`}
                        id="ios-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#ios"
                        type="button"
                        role="tab"
                      >
                        iPhone and iPad
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${platform.isAndroid ? 'active' : ''}`}
                        id="android-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#android"
                        type="button"
                        role="tab"
                      >
                        Android
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${platform.isDesktop ? 'active' : ''}`}
                        id="desktop-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#desktop"
                        type="button"
                        role="tab"
                      >
                        Desktop
                      </button>
                    </li>
                  </ul>

             <div className="tab-content">
  <div
    className={`tab-pane fade ${platform.isIOS ? 'show active' : ''}`}
    id="ios"
    role="tabpanel"
    aria-labelledby="ios-tab"
  >
    <ol className="list-unstyled mb-0 brand-text-gray">
      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <BrowserSafari />
        </span>
        <span>Open this page in Safari</span>
      </li>

      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <Upload />
        </span>
        <span>Tap the Share button</span>
      </li>

      <li className="d-flex align-items-start">
        <span className="me-3 mt-1">
          <PlusSquare />
        </span>
        <span>Tap Add to Home Screen</span>
      </li>
    </ol>

    {!platform.isSafari && platform.isIOS ? (
      <div className="alert alert-warning mt-4 mb-0">
        Install on iPhone and iPad works best in Safari.
      </div>
    ) : null}
  </div>

  <div
    className={`tab-pane fade ${platform.isAndroid ? 'show active' : ''}`}
    id="android"
    role="tabpanel"
    aria-labelledby="android-tab"
  >
    {canPromptInstall ? (
      <div className="alert alert-info mb-4">
        Use the Install app button above for one step install.
      </div>
    ) : (
      <div className="alert alert-light border mb-4">
        If the install button is disabled, use the browser menu option.
      </div>
    )}

    <ol className="list-unstyled mb-0 brand-text-gray">
      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <BrowserChrome />
        </span>
        <span>Open this page in Chrome</span>
      </li>

      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <ThreeDotsVertical />
        </span>
        <span>Tap the menu button</span>
      </li>

      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <BoxArrowDown />
        </span>
        <span>Tap Add to home screen</span>
      </li>

      <li className="d-flex align-items-start">
        <span className="me-3 mt-1">
          <Check />
        </span>
        <span>Confirm install</span>
      </li>
    </ol>
  </div>

  <div
    className={`tab-pane fade ${platform.isDesktop ? 'show active' : ''}`}
    id="desktop"
    role="tabpanel"
    aria-labelledby="desktop-tab"
  >
    <ol className="list-unstyled mb-0 brand-text-gray">
      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <BrowserChrome />
        </span>
        <span>Open this site in Chrome or Edge</span>
      </li>

      <li className="d-flex align-items-start mb-3">
        <span className="me-3 mt-1">
          <BoxArrowDown />
        </span>
        <span>Select the install icon in the address bar</span>
      </li>

      <li className="d-flex align-items-start">
        <span className="me-3 mt-1">
          <Check />
        </span>
        <span>Select Install</span>
      </li>
    </ol>
  </div>
</div>

                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="bg-white border rounded-4 shadow-sm h-100">
                <div className="p-4 p-lg-5">
                  <h2 className="h4 fw-semibold brand-text-dark mb-3">What you get</h2>

                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div className="contact-icon flex-shrink-0">
                      <Check />
                    </div>
                    <div>
                      <div className="fw-semibold brand-text-dark">Faster access</div>
                      <div className="small brand-text-gray">Open from your home screen in one tap.</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div className="contact-icon flex-shrink-0">
                      <Check />
                    </div>
                    <div>
                      <div className="fw-semibold brand-text-dark">App style experience</div>
                      <div className="small brand-text-gray">Cleaner navigation with fewer distractions.</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-start gap-3">
                    <div className="contact-icon flex-shrink-0">
                      <Check />
                    </div>
                    <div>
                      <div className="fw-semibold brand-text-dark">Offline support</div>
                      <div className="small brand-text-gray">Key screens can still load when connection drops.</div>
                    </div>
                  </div>

                  <hr className="my-4 footer-divider" />

                  <div className="small brand-text-gray">
                    If you already installed the app, open it from your home screen or app list.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center small brand-text-gray mt-4 mb-4">
            iRise Psychology
          </div>
        </div>
      </div>

      <div className="bg-dark py-4" />
    </div>
  )
}
